import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

// 지금 프로덕션(OCI dashboard / analyser / trader + CF)을 전부 AWS에 올렸을 때의 목표 맵.
// 아래 "AWS Architecture Example" 은 옛 학습용 샘플 — 이 다이어그램이 현재 운영 역할 기준.
const AWS_FULL_PROD_DIAGRAM = `
graph TB
    subgraph Edge["Edge"]
        User([User])
        R53[Route 53]
        CF[CloudFront]
        ACM[ACM TLS]
    end

    subgraph Static["Static SPA"]
        S3[S3<br/>mercury frontend]
    end

    subgraph Compute["Compute VPC"]
        ALB[ALB]
        Dash[EC2 or ECS<br/>dashboard nginx proxy]
        Analyser[EC2 or ECS<br/>analyser API]
        Trader[EC2<br/>live trader]
        Collect[EC2 or ECS<br/>WS collectors]
    end

    subgraph Data["Data"]
        RDS[(RDS Aurora<br/>or Postgres)]
        TS[(Timescale<br/>marks OI alerts)]
        S3data[S3<br/>tick archive]
    end

    subgraph Sched["Ops"]
        EB[EventBridge]
        CW[CloudWatch]
    end

    subgraph Ext["External venues"]
        Binance[Binance]
        Upbit[Upbit]
        Kiwoom[Kiwoom KRX]
    end

    User -->|HTTPS| R53
    R53 --> CF
    ACM -.-> CF
    CF -->|SPA| S3
    CF -->|API paths| ALB
    ALB --> Dash
    Dash --> Analyser
    Dash --> Trader
    Analyser --> TS
    Analyser --> RDS
    Trader --> RDS
    Trader --> Analyser
    Collect --> TS
    Collect --> S3data
    EB -->|daily jobs| Analyser
    EB -->|retune timers| Trader
    CW --- Dash
    CW --- Analyser
    CW --- Trader
    Binance --> Collect
    Binance --> Trader
    Upbit --> Collect
    Upbit --> Trader
    Kiwoom --> Trader

    style User fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style R53 fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style CF fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style ACM fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style S3 fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style ALB fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style Dash fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style Analyser fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style Trader fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style Collect fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style RDS fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style TS fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style S3data fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style EB fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style CW fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style Binance fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style Upbit fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style Kiwoom fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
`;

// 맨 위 표시용 AWS 아키텍처 예시 (architecture-beta + config)
// AWS 아이콘 이름: https://github.com/awslabs/aws-icons-for-plantuml (dist/aws-icons-mermaid.json)
const TOP_DIAGRAM = `
%%{init: {'theme': 'base', 'themeVariables': {'darkMode': false, 'archEdgeColor': '#232F3E', 'archEdgeArrowColor': '#232F3E', 'archGroupBorderColor': '#7D8998'}}}%%
architecture-beta
  service user(aws:user)[User]
  group awscloud(aws:aws-cloud)[AWS Cloud]
  group region(aws:region)[Region] in awscloud

  group s3bucket(aws:simple-storage-service)[Amazon S3 bucket] in region

    service video(aws:multimedia)[video] in s3bucket
    service frontend(aws:generic-application)[frontend] in s3bucket
    service transcript(aws:documents)[transcript] in s3bucket

    user:R -[1 upload]-> L:video
    video:R --> L:frontend
    frontend:R --> L:transcript

  service handler(aws:lambda-lambda-function)[ObjectCreated event handler] in region
  service lambda2(aws:lambda-lambda-function)[Lambda] in region
  service ws(aws:data-stream)[WebSocket] in region
  service transcribe(aws:transcribe)[Amazon Transcribe] in region

  group ec2group(aws:ec2)[EC2] in region
    service mongodb(aws:documentdb)[MongoDB] in ec2group

  handler:T <-[2]- B:video
  lambda2:T -[WS]-> B:frontend
  frontend:R -[WS]-> L:lambda2
  lambda2:R --> L:mongodb
  mongodb:R --> L:lambda2
  lambda2:R --> L:ws
  ws:R --> L:lambda2
  transcribe:T -[4]-> B:transcript

  group workflow(aws:step-functions-workflow)[AWS Step Functions workflow] in region

    service extractaudio(aws:lambda-lambda-function)[extract audio] in workflow
    service transcribeaudio(aws:lambda-lambda-function)[transcribe audio] in workflow

    extractaudio:R --> L:transcribeaudio
    extractaudio{group}:L <-[Start Execution]- B:handler
    extractaudio:T --> B:lambda2
    transcribeaudio:T --> B:transcribe
`;

// 프로덕션 AWS 아키텍처 (graph TB: 영역 + 엣지 라벨 HTTPS/WS)
const PROD_DIAGRAM = `
graph TB
    subgraph Frontend["Frontend"]
        Internet([Internet])
        Route53[Route53]
        CloudFront[CloudFront]
        S3[S3<br/>mercury_frontend]
    end

    subgraph Backend["Backend"]
        Lambda[Lambda<br/>mercury_stock]
        WebSocket[WebSocket]
        RDS[(RDS<br/>mercury_db)]
        EC2[EC2]
        Redis[(Redis)]
        RabbitMQ[RabbitMQ]
        MongoDB[(MongoDB)]
    end

    subgraph External["External Data Sources"]
        KRX[KRX Market Data]
    end

    Internet -->|HTTPS| Route53
    Route53 -->|HTTPS| CloudFront
    CloudFront -->|HTTPS| S3
    CloudFront -->|HTTPS| Lambda
    CloudFront -->|WS| WebSocket
    WebSocket --> Lambda
    Lambda -->|Read/Write| RDS
    EC2 --> Redis
    EC2 --> RabbitMQ
    EC2 --> MongoDB
    Lambda -->|Read/Write| Redis
    Lambda -->|Consume| RabbitMQ
    Lambda -->|Read/Write| MongoDB
    KRX -->|Market Data| Lambda

    style Internet fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style Route53 fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style CloudFront fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style S3 fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style Lambda fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style WebSocket fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style RDS fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style EC2 fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style Redis fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style RabbitMQ fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style MongoDB fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style KRX fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
`;

// 기존 개념 구조 다이어그램 (graph TB)
const CONCEPTUAL_DIAGRAM = `
graph TB
    subgraph "Frontend"
        ReactApp[React App<br/>Nginx :80/:443]
        LandingPage[Landing Page]
        Dashboard[Dashboard Pages]
    end

    subgraph "Backend Services"
        Backend[mercury-backend<br/>Go :8080]
        WebSocket[WebSocket Server<br/>/api/dostk/websocket]
    end

    subgraph "Data Storage"
        Redis[(Redis<br/>:6379)]
        MySQL[(MySQL/RDS<br/>:3306)]
    end

    subgraph "Message Queue"
        RabbitMQ[RabbitMQ<br/>:5672]
    end

    subgraph "External Data Sources"
        KRX[KRX Market Data]
        Upbit[Upbit API]
    end

    ReactApp -->|HTTP API| Backend
    ReactApp -->|WebSocket| WebSocket
    Backend -->|Read/Write| Redis
    Backend -->|Read/Write| MySQL
    Backend -->|Consume| RabbitMQ
    RabbitMQ -->|Save| MySQL

    KRX -->|Market Data| Backend
    Upbit -->|Ticker Data| Backend

    Backend -->|Store| Redis
    Redis -->|Stream Data| WebSocket
    WebSocket -->|Real-time| ReactApp

    MySQL -->|Import| Backend
    Backend -->|Backtest Data| Redis
    Redis -->|HTTP API| ReactApp

    style ReactApp fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style Backend fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style WebSocket fill:#93c5fd,stroke:#f9a8d4,stroke-width:2px,color:#161616
    style Redis fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style MySQL fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style RabbitMQ fill:#f9a8d4,stroke:#86efac,stroke-width:2px,color:#161616
    style KRX fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
    style Upbit fill:#86efac,stroke:#93c5fd,stroke-width:2px,color:#161616
`;

// Mercury 시스템 아키텍처 (architecture-beta) — mercury-frontend / mercury-backend / mercury-stock 중심
// 주의: architecture-beta 의 [라벨]에는 하이픈/점 등 특수문자가 들어갈 수 없음 (영문자+공백 위주)
// 주의: 최상위 엔티티가 많으면 SVG가 좌우로 폭발해 모두 작아짐 — TOP_DIAGRAM처럼 user + awscloud 위주로 단순화
const MERCURY_DIAGRAM = `
%%{init: {'theme': 'base', 'themeVariables': {'darkMode': false, 'archEdgeColor': '#232F3E', 'archEdgeArrowColor': '#232F3E', 'archGroupBorderColor': '#7D8998'}}}%%
architecture-beta
  service user(aws:user)[User]

  group awscloud(aws:aws-cloud)[AWS Cloud]
  group region(aws:region)[Region] in awscloud

  service cf(aws:generic-application)[CloudFront] in region
  service acm(aws:generic-application)[ACM] in region

  group s3bucket(aws:simple-storage-service)[S3 mercury frontend] in region
    service spa(aws:generic-application)[mercury frontend SPA] in s3bucket

  service apigw(aws:data-stream)[WebSocket API Gateway] in region
  service rds(aws:documentdb)[RDS MySQL] in region

  group ec2group(aws:ec2)[EC2 prod market hours] in region
    service stock(aws:generic-application)[mercury stock auto trader] in ec2group
    service backend(aws:generic-application)[mercury backend BT] in ec2group
    service redis(aws:documentdb)[Redis] in ec2group
    service rabbit(aws:generic-application)[RabbitMQ] in ec2group
    service mongo(aws:documentdb)[MongoDB] in ec2group

  group external(aws:generic-application)[External]
    service dns(aws:generic-application)[DNS] in external
    service kiwoom(aws:generic-application)[Kiwoom API] in external
    service opc(aws:generic-application)[OPC Server] in external

  user:R -[HTTPS]-> L:dns
  dns:R --> L:cf
  acm:B -[TLS]-> T:cf
  cf:B --> T:spa
  cf:R --> L:backend
  cf:R -[WS]-> L:apigw
  apigw:R --> L:backend

  backend:R --> L:rds
  stock:R --> L:rds
  kiwoom:R --> L:stock
  opc:R --> L:stock
`;

// SLA 추정 패널 (Mercury System 다이어그램 아래 표시)
// 가정:
// - mercury 시스템 전체가 한국 장중에만 운영 (KST 09:00-15:30, ~250 trading days/year)
// - 비-장중 시간엔 EC2가 의도적으로 정지 (mercury-stop-ec2 람다) → 다운타임 아님
// - mercury-stock 자동매매 + mercury BT는 같은 EC2에서 콜로케이션
// - RDS Single-AZ, EC2 단일 인스턴스, Redis/Mongo/RabbitMQ는 EC2 위 자체 호스팅
const KOREAN_MARKET_HOURS_PER_YEAR = 6.5 * 250; // ~1625h

type Component = { name: string; sla: number; note?: string };
type PathStep = { label: string; sla: number };
type Path = { name: string; chain: string; steps: PathStep[]; window: 'market' | 'allTime'; criticality: 'critical' | 'aux'; externalNote?: string };

const SLA_COMPONENTS: Component[] = [
  { name: 'Google Cloud DNS', sla: 100.0 },
  { name: 'CloudFront', sla: 99.9 },
  { name: 'S3 Standard', sla: 99.9 },
  { name: 'API Gateway (WS)', sla: 99.95 },
  { name: 'RDS Single-AZ', sla: 99.95, note: 'Multi-AZ면 99.99' },
  { name: 'EC2 single instance', sla: 99.5, note: 'ASG·Multi-AZ면 99.99 — 모든 장중 경로의 병목' },
  { name: 'Self-hosted Redis/Mongo/RabbitMQ', sla: 99.5, note: 'EC2와 운명공동체' },
];

const SLA_PATHS: Path[] = [
  {
    name: 'mercury-stock 자동매매 (장중)',
    chain: 'Kiwoom → EC2(stock + DBs) → RDS',
    steps: [
      { label: 'EC2', sla: 99.5 },
      { label: 'RDS', sla: 99.95 },
    ],
    window: 'market',
    criticality: 'critical',
    externalNote: 'Kiwoom API SLA 미공개, 장중 의존',
  },
  {
    name: 'mercury BT — HTTP API (장중)',
    chain: 'DNS → CloudFront → EC2(backend) → RDS',
    steps: [
      { label: 'DNS', sla: 100 },
      { label: 'CloudFront', sla: 99.9 },
      { label: 'EC2', sla: 99.5 },
      { label: 'RDS', sla: 99.95 },
    ],
    window: 'market',
    criticality: 'aux',
  },
  {
    name: 'mercury BT — WebSocket (장중)',
    chain: 'API GW → EC2(backend) → RDS',
    steps: [
      { label: 'API GW', sla: 99.95 },
      { label: 'EC2', sla: 99.5 },
      { label: 'RDS', sla: 99.95 },
    ],
    window: 'market',
    criticality: 'aux',
  },
  {
    name: '정적 랜딩 페이지 (24×7)',
    chain: 'DNS → CloudFront → S3',
    steps: [
      { label: 'DNS', sla: 100 },
      { label: 'CloudFront', sla: 99.9 },
      { label: 'S3', sla: 99.9 },
    ],
    window: 'allTime',
    criticality: 'aux',
  },
];

// 가용성 곱 (퍼센트 입력 → 퍼센트 출력)
const computeSla = (steps: PathStep[]): number =>
  steps.reduce((acc, s) => acc * (s.sla / 100), 1) * 100;

// 운영 윈도우 기준 연간 다운타임 (시간)
const computeOperationalDowntimeHr = (slaPercent: number, window: 'market' | 'allTime'): number => {
  const annualHours = window === 'market' ? KOREAN_MARKET_HOURS_PER_YEAR : 365 * 24;
  return annualHours * (1 - slaPercent / 100);
};

const SlaPanel: React.FC = () => {
  const card: React.CSSProperties = {
    backgroundColor: '#262626',
    border: '1px solid #3a3a3a',
    borderRadius: '8px',
    padding: '16px',
    color: '#e5e7eb',
  };
  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #3a3a3a', color: '#cbd5e1', fontWeight: 600 };
  const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #2a2a2a', color: '#e5e7eb' };

  const slaColor = (s: number) => {
    if (s >= 99.95) return '#86efac';
    if (s >= 99.5) return '#fde68a';
    return '#fca5a5';
  };

  return (
    <div style={{ width: '95%', maxWidth: '1400px', marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <h3 style={{ color: '#fff', margin: '0', fontSize: '1.1rem' }}>SLA 추정 (AWS 공식 SLA × AND)</h3>
      <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '-8px' }}>
        장중 경로는 <b>한국 장중 시간 ({KOREAN_MARKET_HOURS_PER_YEAR}h/년)</b> 기준 다운타임. 비-장중 시간엔 EC2가 의도 정지(`mercury-stop-ec2` 람다)이라 다운타임으로 보지 않음.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '12px' }}>
        {SLA_PATHS.map((p) => {
          const sla = computeSla(p.steps);
          const downHr = computeOperationalDowntimeHr(sla, p.window);
          const formula = p.steps.map((s) => `${s.label} ${s.sla}`).join(' × ');
          const windowLabel = p.window === 'market' ? `장중 ${KOREAN_MARKET_HOURS_PER_YEAR}h/년` : `24×7 8760h/년`;
          const isCritical = p.criticality === 'critical';
          return (
            <div
              key={p.name}
              style={{
                ...card,
                borderColor: isCritical ? '#ef4444' : '#3a3a3a',
                borderWidth: isCritical ? '2px' : '1px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                <div style={{ fontSize: '0.9rem', color: '#9ca3af' }}>{p.name}</div>
                {isCritical && (
                  <span
                    style={{
                      fontSize: '0.7rem',
                      backgroundColor: '#7f1d1d',
                      color: '#fecaca',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    CRITICAL
                  </span>
                )}
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: slaColor(sla) }}>{sla.toFixed(2)}%</div>
              <div
                style={{
                  fontSize: '0.8rem',
                  color: '#cbd5e1',
                  marginTop: '6px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  wordBreak: 'break-word',
                }}
              >
                {formula} = {sla.toFixed(2)}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '6px' }}>
                연 ~{downHr.toFixed(1)}h 다운 / 운영창 {windowLabel}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '8px' }}>{p.chain}</div>
              {p.externalNote && (
                <div style={{ fontSize: '0.75rem', color: '#fbbf24', marginTop: '6px' }}>※ {p.externalNote}</div>
              )}
            </div>
          );
        })}
      </div>

      <div style={card}>
        <div style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>컴포넌트별 SLA</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr>
              <th style={th}>Component</th>
              <th style={{ ...th, width: '120px' }}>SLA</th>
              <th style={th}>비고</th>
            </tr>
          </thead>
          <tbody>
            {SLA_COMPONENTS.map((c) => (
              <tr key={c.name}>
                <td style={td}>{c.name}</td>
                <td style={{ ...td, color: slaColor(c.sla), fontWeight: 600 }}>{c.sla.toFixed(2)}%</td>
                <td style={{ ...td, color: '#9ca3af' }}>{c.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ ...card, borderColor: '#7f1d1d', backgroundColor: '#1f1414' }}>
        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#fca5a5', marginBottom: '8px' }}>약한 고리 (개선 우선순위)</div>
        <ol style={{ margin: 0, paddingLeft: '20px', color: '#e5e7eb', fontSize: '0.9rem', lineHeight: '1.7' }}>
          <li><b>EC2 단일 인스턴스 (99.5%)</b> — HTTP API · WS 경로의 병목. ASG+Multi-AZ로 99.99% 가능</li>
          <li><b>EC2 위 자체 호스팅 DB</b> — MongoDB→DocumentDB / Redis→ElastiCache / RabbitMQ→Amazon MQ 로 옮기면 99.95%+</li>
          <li><b>RDS Single-AZ (99.95%)</b> → Multi-AZ로 99.99%</li>
          <li><b>외부 의존성</b> — Kiwoom API · OPC 서버는 SLA 보장 불가, 재시도·큐잉·폴백으로 완화</li>
        </ol>
        <div style={{ marginTop: '12px', fontSize: '0.85rem', color: '#9ca3af' }}>
          ※ 이론값(공식 SLA 곱)이라 실측치와 다를 수 있음. 배포 실수, 인증서 만료, 코드 버그, Kiwoom 장애 등은 별도 리스크.
        </div>
      </div>
    </div>
  );
};

/** Temporarily hide older sample diagrams + SLA panel (keep AWS full-prod only). */
const SHOW_LEGACY_ARCHITECTURE = false;

const SystemArchitecture: React.FC = () => {
  const awsFullProdRef = useRef<HTMLDivElement>(null);
  const topDiagramRef = useRef<HTMLDivElement>(null);
  const prodRef = useRef<HTMLDivElement>(null);
  const conceptualRef = useRef<HTMLDivElement>(null);
  const mercuryRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'dark',
        themeVariables: {
          primaryColor: '#86efac',
          primaryTextColor: '#161616',
          primaryBorderColor: '#93c5fd',
          lineColor: '#f9a8d4',
          secondaryColor: '#262626',
          tertiaryColor: '#333333',
          background: '#1a1a1a',
          mainBkg: '#262626',
          secondBkg: '#2a2a2a',
          textColor: '#ffffff',
          secondaryTextColor: '#cccccc',
          tertiaryTextColor: '#888888',
          fontSize: '18px',
          fontFamily: 'inherit',
        },
        flowchart: { useMaxWidth: false, htmlLabels: true },
      });

      // AWS 아이콘: https://github.com/awslabs/aws-icons-for-plantuml (experimental dist/aws-icons-mermaid.json)
      try {
        await mermaid.registerIconPacks([
          {
            name: 'logos',
            loader: () =>
              fetch('https://unpkg.com/@iconify-json/logos@1.2.4/icons.json').then((res) => res.json()),
          },
          {
            name: 'aws',
            loader: () =>
              fetch(
                'https://raw.githubusercontent.com/awslabs/aws-icons-for-plantuml/main/dist/aws-icons-mermaid.json'
              ).then((res) => res.json()),
          },
        ]);
      } catch (e) {
        if (!cancelled) setError('아이콘 로드 실패');
      }

      if (awsFullProdRef.current && !cancelled) {
        try {
          const { svg } = await mermaid.render('aws-full-prod', AWS_FULL_PROD_DIAGRAM.trim());
          if (cancelled) return;
          awsFullProdRef.current.innerHTML = svg;
        } catch (e) {
          if (!cancelled) setError(e instanceof Error ? e.message : 'AWS full-prod 다이어그램 렌더 실패');
        }
      }

      if (SHOW_LEGACY_ARCHITECTURE && topDiagramRef.current && !cancelled) {
        try {
          const { svg } = await mermaid.render('top-arch', TOP_DIAGRAM.trim());
          if (cancelled) return;
          topDiagramRef.current.innerHTML = svg;
        } catch (e) {
          if (!cancelled) setError(e instanceof Error ? e.message : '상단 다이어그램 렌더 실패');
        }
      }

      if (SHOW_LEGACY_ARCHITECTURE && prodRef.current && !cancelled) {
        try {
          const { svg } = await mermaid.render('prod-arch', PROD_DIAGRAM.trim());
          if (cancelled) return;
          prodRef.current.innerHTML = svg;
          setError(null);
        } catch (e) {
          if (!cancelled) setError(e instanceof Error ? e.message : '다이어그램 렌더 실패');
        }
      }

      if (SHOW_LEGACY_ARCHITECTURE && conceptualRef.current && !cancelled) {
        try {
          const { svg } = await mermaid.render('conceptual-arch', CONCEPTUAL_DIAGRAM.trim());
          if (cancelled) return;
          conceptualRef.current.innerHTML = svg;
        } catch (_) {
          // 개념 다이어그램 실패는 무시 (상단 에러만 표시)
        }
      }

      if (SHOW_LEGACY_ARCHITECTURE && mercuryRef.current && !cancelled) {
        try {
          const { svg } = await mermaid.render('mercury-arch', MERCURY_DIAGRAM.trim());
          if (cancelled) return;
          mercuryRef.current.innerHTML = svg;
        } catch (e) {
          if (!cancelled) {
            mercuryRef.current.innerHTML = `<pre style="color:#f87171;white-space:pre-wrap;">Mercury 다이어그램 렌더 실패: ${e instanceof Error ? e.message : String(e)}</pre>`;
          }
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const diagramContainerStyle: React.CSSProperties = {
    minHeight: '400px',
    width: '95%',
    maxWidth: '1400px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  return (
    <>
      <style>{`
        .system-architecture-diagram svg {
          width: 100%;
          height: auto;
          max-width: 100%;
        }
      `}</style>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', overflow: 'auto', width: '100%' }}>
      <h2 style={{ color: '#fff', marginBottom: '8px', fontSize: '1.5rem' }}>
        Current production → all on AWS
      </h2>
      <p style={{ color: '#9ca3af', fontSize: '0.9rem', margin: '0 0 16px', textAlign: 'center', maxWidth: 720 }}>
        Maps today&apos;s roles (dashboard SPA, analyser API + DB, live trader, collectors)
        onto AWS. Venues stay external.
      </p>
      {error && <p style={{ color: '#f87171' }}>{error}</p>}
      <div
        ref={awsFullProdRef}
        className="system-architecture-diagram"
        style={diagramContainerStyle}
      />
      {SHOW_LEGACY_ARCHITECTURE && (
        <>
          <h2 style={{ color: '#fff', marginTop: '48px', marginBottom: '16px', fontSize: '1.5rem' }}>AWS Architecture Example</h2>
          <div
            ref={topDiagramRef}
            className="system-architecture-diagram"
            style={diagramContainerStyle}
          />
          <h2 style={{ color: '#fff', marginTop: '48px', marginBottom: '16px', fontSize: '1.5rem' }}>Production (AWS)</h2>
          <div
            ref={prodRef}
            className="system-architecture-diagram"
            style={diagramContainerStyle}
          />
          <h2 style={{ color: '#fff', marginTop: '48px', marginBottom: '16px', fontSize: '1.5rem' }}>Conceptual Architecture</h2>
          <div
            ref={conceptualRef}
            className="system-architecture-diagram"
            style={diagramContainerStyle}
          />
          <h2 style={{ color: '#fff', marginTop: '48px', marginBottom: '16px', fontSize: '1.5rem' }}>Mercury System (Production)</h2>
          <div
            ref={mercuryRef}
            className="system-architecture-diagram"
            style={diagramContainerStyle}
          />
          <SlaPanel />
        </>
      )}
    </div>
    </>
  );
};

export default SystemArchitecture;
